<section class="templates-page">
  <!-- Top Bar: Page Title + Subtitle on left, New Template button on right -->
  <header class="page-topbar">
    <div class="topbar-title-block">
      <h1 class="page-title">Templates</h1>
      <p class="page-subtitle">Manage and reuse your document templates</p>
    </div>

    <a class="btn-purple" routerLink="/templates/new">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
        <line x1="12" y1="5" x2="12" y2="19"></line>
        <line x1="5" y1="12" x2="19" y2="12"></line>
      </svg>
      <span>New Template</span>
    </a>
  </header>

  <div class="templates-body">
    <!-- Search & Stats Toolbar -->
    <div class="search-bar-row">
      <div class="search-field-wrap">
        <input
          type="text"
          [(ngModel)]="searchQuery"
          placeholder="Search Templates..."
          (keyup.enter)="applySearch()"
        />

        @if (searchQuery) {
          <button
            type="button"
            class="search-clear-btn"
            (click)="clearSearch()"
            [disabled]="loading"
            aria-label="Clear search"
            title="Clear search"
          >
            ×
          </button>
        }

        <button
          type="button"
          class="search-icon-btn"
          (click)="applySearch()"
          [disabled]="loading"
          aria-label="Search"
          title="Search"
        >
          <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <circle cx="9" cy="9" r="5.5"></circle>
            <line x1="13.5" y1="13.5" x2="17.5" y2="17.5"></line>
          </svg>
        </button>
      </div>

      <div class="total-templates-count">
        <strong>{{ total | number }}</strong>
        <span>{{ totalLabel() }}</span>
      </div>
    </div>

    @if (errorMessage) {
      <div class="alert-banner error">
        <p class="status error">{{ errorMessage }}</p>
      </div>
    }

    <!-- Templates Table Card -->
    <div class="c-table-card">
      <table class="c-table">
        <thead>
          <tr>
            <th class="name-col">NAME</th>
            <th class="desc-col">DESCRIPTION</th>
            <th class="fields-col">FIELDS</th>
            <th class="date-col">LAST MODIFIED</th>
            <th class="actions-th">
              <span class="sr-only">Actions</span>
            </th>
          </tr>
        </thead>
        <tbody>
          @if (loading) {
            <tr>
              <td colspan="5" class="c-table-loading-cell empty-row">
                <div class="c-loading-state">
                  <div class="c-spinner"></div>
                  <p class="c-loading-text">Loading templates…</p>
                </div>
              </td>
            </tr>
          } @else if (templates.length === 0) {
            <tr>
              <td colspan="5" class="empty-row">
                <div class="empty-state-wrap">
                  <div class="empty-state-icon">
                    <svg viewBox="0 0 24 24" width="26" height="26" fill="none" stroke="currentColor" stroke-width="1.5">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                      <polyline points="14 2 14 8 20 8"></polyline>
                      <line x1="16" y1="13" x2="8" y2="13"></line>
                      <line x1="16" y1="17" x2="8" y2="17"></line>
                      <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                  </div>
                  <p class="empty-state-text">No templates found.</p>
                  <p class="empty-state-sub">Create your first template to quickly prepare documents for signing.</p>
                  <a class="btn-purple" routerLink="/templates/new">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                    <span>New Template</span>
                  </a>
                </div>
              </td>
            </tr>
          } @else {
            @for (template of templates; track template._id) {
              <tr
                class="template-row"
                [attr.data-row-id]="template._id"
                (click)="onRowClick(template._id, $event)"
              >
                <td class="name-cell">
                  <div class="c-cell-title">
                    <a
                      class="title"
                      [routerLink]="['/templates', template._id, 'edit']"
                      (click)="$event.stopPropagation()"
                    >
                      {{ template.name }}
                    </a>
                  </div>
                </td>
                <td class="desc-cell">
                  <span class="desc-text">{{ template.description || '—' }}</span>
                </td>
                <td class="fields-cell">
                  <span class="c-badge-circle" [title]="template.fields.length + ' fields'">
                    {{ template.fields.length }}
                  </span>
                </td>
                <td class="date-cell">
                  <span class="date-text">{{ formatUpdatedAt(template.updatedAt) }}</span>
                </td>
                <td class="actions-cell">
                  <a
                    class="edit-link"
                    [routerLink]="['/templates', template._id, 'edit']"
                    aria-label="Edit template"
                    title="Edit template"
                    (click)="$event.stopPropagation()"
                  >
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                    </svg>
                    <span>Edit</span>
                  </a>
                </td>
              </tr>
            }
          }
        </tbody>
      </table>
    </div>

    <!-- Bottom Pagination (styled exactly like Documents list) -->
    <footer class="pagination-footer pager">
      <div class="pagination-left">
        <span>Showing</span>
        <div class="per-page-select-wrap">
          <select [ngModel]="limit" (ngModelChange)="onLimitChange($event)" [disabled]="loading">
            @for (lim of availableLimits; track lim) {
              <option [value]="lim">{{ lim }}</option>
            }
          </select>
          <svg class="select-chevron" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="6 8 10 12 14 8"></polyline>
          </svg>
        </div>
        <span>per page of <strong>{{ total | number }}</strong></span>
      </div>

      <div class="pagination-right pager-actions">
        <div class="page-nav-controls">
          <button
            type="button"
            class="nav-btn"
            (click)="goToFirstPage()"
            [disabled]="loading || page <= 1"
            title="First page"
            aria-label="First page"
          >
            ««
          </button>
          <button
            type="button"
            class="nav-btn ghost"
            (click)="goToPreviousPage()"
            [disabled]="loading || page <= 1"
            title="Previous page"
            aria-label="Previous page"
          >
            ‹
          </button>

          @for (p of pageNumbers(); track p) {
            <button
              type="button"
              class="page-num-btn"
              [class.active]="p === page"
              (click)="goToPage(p)"
              [disabled]="loading"
            >
              {{ p }}
            </button>
          }

          <button
            type="button"
            class="nav-btn ghost"
            (click)="goToNextPage()"
            [disabled]="loading || page >= totalPages"
            title="Next page"
            aria-label="Next page"
          >
            ›
          </button>
          <button
            type="button"
            class="nav-btn"
            (click)="goToLastPage()"
            [disabled]="loading || page >= totalPages"
            title="Last page"
            aria-label="Last page"
          >
            »»
          </button>
        </div>

        <div class="goto-page-wrap">
          <span class="goto-label">Go To Page</span>
          <input
            type="number"
            min="1"
            [max]="totalPages"
            [(ngModel)]="goToPageInput"
            (keyup.enter)="jumpToPage()"
            [disabled]="loading"
            aria-label="Target page number"
          />
          <button
            type="button"
            class="goto-btn btn-outline"
            (click)="jumpToPage()"
            [disabled]="loading"
          >
            Go
          </button>
        </div>
      </div>
    </footer>
  </div>
</section>
